/**
 * Phase 322a — Public Get Started page.
 *
 * The single funnel entry point a prospect lands on after clicking a
 * pricing CTA. Collects 4 fields, hits `/api/signup/create-payment-link`,
 * then redirects to the Razorpay-hosted payment page. On successful
 * payment the webhook auto-creates their academy + emails them a magic
 * link to /onboarding.
 *
 * Anti-funnel-leakage:
 *   - Inline error handling (no scary alerts)
 *   - Disabled submit while loading
 *   - Surfaces the specific backend error (e.g. "academy_already_exists")
 *     instead of a generic "something went wrong"
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Loader2, CheckCircle2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_PLAN = {
  name: 'Out-Play Growth',
  price_inr: 2999,
  features: [
    'Unlimited students & batches',
    'Auto fee invoicing (Razorpay)',
    'WhatsApp QR self-signup',
    'Attendance, scorecards, parent portal',
    'AI Activation & Engagement nudges',
  ],
};

export default function GetStarted() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', name: '', phone: '', academy_name: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Phase 322b — Fire a single get_started_view beacon on mount.
  // Best-effort, never blocks render. correlation_key is the visitor's
  // session storage so dedup works across SPA navigations.
  useEffect(() => {
    try {
      let visitorId = sessionStorage.getItem('outplay_visitor_id');
      if (!visitorId) {
        visitorId = `vis_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
        sessionStorage.setItem('outplay_visitor_id', visitorId);
      }
      fetch(`${API}/api/telemetry/funnel-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: 'get_started_view',
          correlation_key: visitorId,
          properties: { referrer: document.referrer || '', utm: window.location.search },
        }),
      }).catch(() => {});
    } catch (_e) { /* never block on telemetry */ }
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/api/signup/create-payment-link`, form);
      if (r.data?.payment_link_url) {
        toast.success('Redirecting to Razorpay…');
        // Slight delay so the toast renders before the redirect.
        setTimeout(() => { window.location.href = r.data.payment_link_url; }, 600);
      } else {
        setError('Unexpected response from server. Please try again.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = detail?.error;
      const hint = detail?.hint;
      const map = {
        invalid_email: 'Please enter a valid email address.',
        name_required: 'Please enter your name.',
        academy_name_required: 'Please enter your academy name.',
        razorpay_keys_not_configured: 'Payments are temporarily disabled. Please email contact@out-play.in.',
        academy_already_exists: hint || 'An academy already exists for this email. Please log in instead.',
        payment_link_create_failed: 'Could not generate your payment link. Please retry in a moment.',
      };
      setError(map[code] || (typeof detail === 'string' ? detail : 'Could not start signup. Please retry.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-emerald-950 via-emerald-900 to-slate-900 px-4 py-12"
      data-testid="get-started-page"
    >
      <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-8 items-start">
        {/* ─────  Left: pitch  ───── */}
        <div className="text-white space-y-6 pt-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30">
            <Sparkles className="h-3.5 w-3.5 text-emerald-300" />
            <span className="text-xs font-semibold tracking-wider text-emerald-200">
              ZERO-TOUCH SIGNUP · LIVE IN 60 SECONDS
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-[1.05] tracking-tight">
            Run your <span className="text-emerald-400">sports academy</span> on autopilot.
          </h1>
          <p className="text-emerald-100/85 text-base leading-relaxed max-w-md">
            Stop chasing fees on WhatsApp and tracking attendance on paper. Out-Play handles
            invoices, parent updates, marketing, and self-signup — so you can spend evenings
            with your athletes, not your spreadsheets.
          </p>

          <div className="space-y-2.5 pt-2" data-testid="get-started-features">
            {PUBLIC_PLAN.features.map((f) => (
              <div key={f} className="flex items-center gap-2.5 text-sm text-emerald-100/90">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                <span>{f}</span>
              </div>
            ))}
          </div>

          <div className="pt-6 text-emerald-200/70 text-xs">
            <span className="font-semibold text-emerald-300">7-day refund policy.</span>{' '}
            Cancel anytime. Cancel within 7 days and we&apos;ll refund every rupee, no questions.
          </div>
        </div>

        {/* ─────  Right: form  ───── */}
        <Card
          className="border-emerald-200/30 bg-white shadow-2xl shadow-emerald-900/30"
          data-testid="get-started-form-card"
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Start your academy</span>
              <span className="text-xs font-normal text-muted-foreground tabular-nums">
                ₹{PUBLIC_PLAN.price_inr.toLocaleString('en-IN')}/mo
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-3.5" data-testid="get-started-form">
              <div>
                <Label htmlFor="academy_name" className="text-xs">Academy name</Label>
                <Input
                  id="academy_name"
                  value={form.academy_name}
                  onChange={update('academy_name')}
                  placeholder="Vidya Bharati FC"
                  required
                  autoFocus
                  data-testid="get-started-academy-name"
                />
              </div>
              <div>
                <Label htmlFor="name" className="text-xs">Your name</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={update('name')}
                  placeholder="Anil Gupta"
                  required
                  data-testid="get-started-name"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  placeholder="you@academy.com"
                  required
                  data-testid="get-started-email"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-xs">WhatsApp number (optional)</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={update('phone')}
                  placeholder="+91 98765 43210"
                  data-testid="get-started-phone"
                />
              </div>

              {error && (
                <div
                  className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800"
                  data-testid="get-started-error"
                >
                  {error}
                  {/login/i.test(error) && (
                    <Button
                      type="button"
                      variant="link"
                      className="px-0 text-red-800 underline"
                      onClick={() => navigate('/login')}
                      data-testid="get-started-error-login-link"
                    >
                      Go to login →
                    </Button>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-6 text-base"
                data-testid="get-started-submit"
              >
                {submitting
                  ? <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  : <ArrowRight className="h-5 w-5 mr-2" />}
                {submitting ? 'Generating secure payment link…' : `Pay ₹${PUBLIC_PLAN.price_inr.toLocaleString('en-IN')} & go live`}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center pt-1">
                You&apos;ll be redirected to Razorpay&apos;s secure payment page. No card details are stored on Out-Play.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
